import React, { useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from 'platform/apollo';

import { ADMIN_CLIENT_UPLOAD_BUCKET } from 'shared/helpers/globals';

import { PreApprovalConfirmation } from 'buying/components/buyDirectCheckout/PreApprovalConfirmation';
import { PreApprovalModule } from 'buying/components/buyDirectCheckout/PreApprovalModule';
import { GetBuyDirectS3Urls } from 'buying/graphql/queries';
import {
  GetBuyDirectS3Urls as GetBuyDirectS3UrlsData,
  GetBuyDirectS3UrlsVariables,
} from 'buying/graphql/queries/__generated__/GetBuyDirectS3Urls';
import { CheckoutState } from 'buying/stateMachines/buyDirectCheckoutFlowStateMachine';

import { FileUpload, FileUploadClient } from 'sellDirect/helpers/FileUploadClient';
import { generateS3Key, S3UploadDestination } from 'sellDirect/helpers/s3Helpers';

const createFileUpload = (file: File, getUrl: string, uploadUrl: string): FileUpload => {
  return {
    file,
    progress: 0,
    getUrl,
    uploadUrl,
    uploadLatency: 0,
    completed: false,
    errorOccurred: false,
    emitMetrics: false,
    errorMessage: '',
  };
};

interface IPreApprovalProps {
  checkoutState: CheckoutState;
  send: (
    args: string | { type: string; value: string | boolean | number | FileUpload | undefined },
  ) => void;
  uploadIdentifier?: string;
}
/**
 * PreApproval step state machine implementation
 * storybook-check-ignore
 */
export const PreApproval: React.FC<IPreApprovalProps> = ({
  checkoutState,
  uploadIdentifier = 'buy_direct',
  send,
}) => {
  const [fetchS3Urls, { data: s3Urls, called }] = useLazyQuery<
    GetBuyDirectS3UrlsData,
    GetBuyDirectS3UrlsVariables
  >(GetBuyDirectS3Urls);
  const [file, setFile] = useState<File | undefined>();
  const [preApprovalDocument, setPreApprovalDocument] = useState<FileUpload | undefined>();
  const [isUploadComplete, setIsUploadComplete] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const reset = () => {
    setFile(undefined);
    setPreApprovalDocument(undefined);
  };

  // Callback function for handling upload progress
  const handleFileUploadOnUpdate = (fileUpload: FileUpload) => {
    // Check for both values otherwise `setIsUploadComplete` is called numerous times
    if (fileUpload.progress === 100 && fileUpload.completed) {
      setIsUploadComplete(true);
      setIsUploading(false);
    } else {
      setIsUploading(true);
    }
    return;
  };

  // Initialize a new FileUploadClient
  const fileUploadClient = useMemo(
    () =>
      new FileUploadClient({
        onUpdate: handleFileUploadOnUpdate,
      }),
    [],
  );

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    reset();
    const fileToUpload = e.currentTarget.files?.[0];
    if (fileToUpload) {
      setFile(fileToUpload);
      // s3://opendoor-admin-client-upload-{env}/uploads/ResaleOfferPortal/{displaySlug}/{file.name}
      const s3Key = generateS3Key(
        fileToUpload,
        uploadIdentifier,
        S3UploadDestination.RESALE_OFFER_PORTAL,
      );

      fetchS3Urls({
        variables: {
          bucketName: ADMIN_CLIENT_UPLOAD_BUCKET,
          documentName: s3Key,
        },
        context: {
          linkContext: {
            team: 'buy-direct',
          },
        },
      });
    }
  };

  useEffect(() => {
    if (called && file && s3Urls) {
      const document = createFileUpload(
        file,
        // @ts-expect-error -- variable is possibly undefined/null
        s3Urls?.seller?.s3DocumentDownloadUrl,
        s3Urls?.seller?.s3DocumentUploadUrl,
      );
      setPreApprovalDocument(document);
      send({ type: 'setMortgagePreapproval', value: document });
      if (checkoutState === CheckoutState.PreApproval) {
        send('FORWARD');
      }
    }
    return () => {};
  }, [called, checkoutState, file, s3Urls, send]);

  useEffect(() => {
    if (isUploadComplete) {
      send('FORWARD');
    }
  }, [isUploadComplete, send]);

  const handleOnBack = () => {
    send('BACK');
    window.scrollTo(0, 0);
  };

  const handleOnForward = () => {
    if (preApprovalDocument && preApprovalDocument.uploadUrl) {
      fileUploadClient.uploadFile(preApprovalDocument, preApprovalDocument.uploadUrl);
    }
  };

  if (checkoutState === CheckoutState.PreApprovalConfirmation && preApprovalDocument) {
    return (
      <PreApprovalConfirmation
        preApprovalDoc={preApprovalDocument}
        onFileChange={onFileChange}
        onForward={handleOnForward}
        onBack={handleOnBack}
        isUploading={isUploading}
      />
    );
  }

  return <PreApprovalModule onBackClick={handleOnBack} onFileChange={onFileChange} />;
};
